import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Header from "@/components/Header";
import { getValidAuthSession, signInWithGoogle, signInWithPassword } from "@/lib/supabaseAuth";
import { X } from "lucide-react";

const GoogleLogo = () => (
  <svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06L5.84 9.9C6.71 7.3 9.14 5.38 12 5.38z"
    />
  </svg>
);

const IniciarSesion = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = searchParams.get("next") || "/";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let isMounted = true;

    getValidAuthSession().then((session) => {
      if (isMounted && session?.access_token) {
        navigate(next, { replace: true });
      }
    });

    return () => {
      isMounted = false;
    };
  }, [navigate, next]);

  const handleFacebookLogin = () => {
    alert("El inicio con Facebook estará disponible pronto. Por ahora puedes entrar con Google o correo.");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setError("");

    try {
      await signInWithPassword({ email, password, remember });
      navigate(next);
    } catch (authError) {
      setError(authError.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Header />
      <main className="min-h-screen bg-slate-50 px-4 py-24">
        <div className="relative mx-auto max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <button
            type="button"
            onClick={() => navigate("/")}
            aria-label="Salir"
            className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition hover:bg-slate-50 hover:text-slate-900"
          >
            <X size={18} />
          </button>
          <h1 className="text-2xl font-black text-slate-950">
            Iniciar sesión
          </h1>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            Ingresa con tu red social o con correo y contraseña en un solo paso.
          </p>

          {searchParams.get("confirmed") && (
            <p className="mt-3 rounded-lg bg-emerald-50 p-3 text-sm font-semibold text-emerald-700">
              Cuenta confirmada. Ya puedes iniciar sesión.
            </p>
          )}

          <button
            type="button"
            onClick={() => signInWithGoogle({ next, mode: "general" })}
            className="mt-5 flex w-full items-center justify-center gap-3 rounded-lg border border-slate-200 bg-white p-3 text-sm font-black text-slate-700 shadow-sm transition hover:bg-slate-50"
          >
            <GoogleLogo />
            Continuar con Google
          </button>

          <button
            type="button"
            onClick={handleFacebookLogin}
            className="mt-3 flex w-full items-center justify-center gap-3 rounded-lg border border-slate-200 bg-white p-3 text-sm font-black text-slate-700 shadow-sm transition hover:bg-slate-50"
          >
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#1877F2] text-sm font-black text-white">
              f
            </span>
            Continuar con Facebook
          </button>

          <div className="my-4 flex items-center gap-3">
            <span className="h-px flex-1 bg-slate-200" />
            <span className="text-xs font-bold uppercase tracking-wide text-slate-400">
              o con correo
            </span>
            <span className="h-px flex-1 bg-slate-200" />
          </div>

          <form onSubmit={handleSubmit} autoComplete="on" className="space-y-3">
            <input
              required
              type="email"
              id="tulocalmx-login-email"
              name="email"
              autoComplete="username"
              placeholder="Correo electrónico"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="w-full rounded-lg border border-slate-200 p-3 outline-none focus:border-[#0056D2]"
            />
            <input
              required
              type="password"
              id="tulocalmx-login-password"
              name="password"
              autoComplete="current-password"
              placeholder="Contraseña"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className="w-full rounded-lg border border-slate-200 p-3 outline-none focus:border-[#0056D2]"
            />

            <label className="flex cursor-pointer items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700">
              <input
                type="checkbox"
                checked={remember}
                onChange={(event) => setRemember(event.target.checked)}
              />
              Recordar inicio de sesión en este dispositivo
            </label>

            {error && (
              <p className="rounded-lg bg-red-50 p-3 text-sm font-semibold text-red-700">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full rounded-lg bg-[#F97316] p-3 font-black text-white disabled:bg-orange-300"
            >
              {isSubmitting ? "Entrando..." : "Iniciar sesión"}
            </button>
          </form>

          <div className="mt-4 flex flex-col gap-3 text-center text-sm">
            <Link to="/recuperar-contrasena" className="font-black text-[#0056D2]">
              Olvidé mi contraseña
            </Link>
            <p className="text-slate-600">
              ¿No tienes cuenta?{" "}
              <Link to="/crear-cuenta" className="font-black text-[#0056D2]">
                Regístrate aquí
              </Link>
            </p>
          </div>

          <p className="mt-5 text-center text-xs leading-5 text-slate-500">
            Al continuar estás aceptando los{" "}
            <Link to="/terminos" className="font-black text-[#0056D2]">
              Términos y condiciones
            </Link>{" "}
            y{" "}
            <Link to="/privacidad" className="font-black text-[#0056D2]">
              Aviso de Privacidad
            </Link>
          </p>
        </div>
      </main>
    </>
  );
};

export default IniciarSesion;
